import { ArrowDownIcon, ArrowUpIcon, DeleteIcon, EditIcon, RepeatIcon, ViewIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Center,
    Checkbox,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import {
    createBsSampleSizeChart,
    deleteBsSampleSizeChart,
    getAllBsSampleSizeCharts,
    getAllBsSampleTypes,
    restoreBsSampleSizeChart,
    updateBsSampleSizeChart
} from '../../BSApi';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import PopoverId from '../../components/PopoverId';
import TitleHeader from '../../components/TitleHeader';
import { hasPermission, useTableColors } from '../../Utils';

const UNIT_OPTIONS = ['ct', 'lb', 'piece', 'g', 'kg', 'oz', 'unit'];
const ROUNDING_OPTIONS = ['none', 'up', 'down', 'nearest'];

const emptyForm = () => ({
    sampleTypeId: '',
    minBatchSize: 0,
    maxBatchSize: 0,
    requiredSampleSize: 0,
    batchUnit: 'ct',
    sampleUnit: 'ct',
    roundingMode: 'none',
    notes: '',
    isActive: true
});

const BSSSampleSizeChartsPage = () => {
    const coolors = useTableColors();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();

    const [items, setItems] = useState([]);
    const [sampleTypes, setSampleTypes] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [newItem, setNewItem] = useState(emptyForm());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleted, setShowDeleted] = useState(false);
    const [sortField, setSortField] = useState('minBatchSize');
    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => { loadData(); }, [showDeleted]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [chartsRes, sampleTypesRes] = await Promise.all([
                getAllBsSampleSizeCharts(showDeleted),
                getAllBsSampleTypes(false)
            ]);

            if (!chartsRes.success) throw new Error(chartsRes.error || 'Failed to load charts');
            if (!sampleTypesRes.success) throw new Error(sampleTypesRes.error || 'Failed to load sample types');

            setItems(Array.isArray(chartsRes.data) ? chartsRes.data : []);
            setSampleTypes(Array.isArray(sampleTypesRes.data) ? sampleTypesRes.data : []);
        } catch (err) {
            setError(err.message || 'Failed to load sample size charts');
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return [...items]
            .filter((item) => {
                const sampleTypeName = item?.sampleTypeId?.name || '';
                return !term || [sampleTypeName, item.batchUnit, item.sampleUnit, item.notes].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));
            })
            .sort((a, b) => {
                const aValue = sortField === 'sampleType' ? a?.sampleTypeId?.name || '' : a?.[sortField] ?? '';
                const bValue = sortField === 'sampleType' ? b?.sampleTypeId?.name || '' : b?.[sortField] ?? '';
                if (typeof aValue === 'number' && typeof bValue === 'number') return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
                const result = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
                return sortOrder === 'asc' ? result : -result;
            });
    }, [items, searchTerm, sortField, sortOrder]);

    const toggleSort = (field) => {
        const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newOrder);
    };

    const handleChange = (setter, field, value) => setter((prev) => ({ ...prev, [field]: value }));
    const openEdit = (item) => {
        setSelectedItem({
            ...item,
            sampleTypeId: item?.sampleTypeId?._id || item?.sampleTypeId || ''
        });
        onOpen();
    };
    const openDelete = (item) => { setSelectedItem(item); onDeleteOpen(); };
    const openView = (item) => { setSelectedItem(item); onViewOpen(); };

    const normalizePayload = (item) => ({
        ...item,
        minBatchSize: Number(item.minBatchSize) || 0,
        maxBatchSize: Number(item.maxBatchSize) || 0,
        requiredSampleSize: Number(item.requiredSampleSize) || 0
    });

    const handleCreate = async () => {
        const response = await createBsSampleSizeChart(normalizePayload(newItem));
        if (response.success) {
            setNewItem(emptyForm());
            onCreateClose();
            loadData();
        } else alert(response.error || 'Failed to create sample size chart');
    };

    const handleSave = async () => {
        const response = await updateBsSampleSizeChart(selectedItem._id, normalizePayload(selectedItem));
        if (response.success) {
            onClose();
            loadData();
        } else alert(response.error || 'Failed to update sample size chart');
    };

    const handleDelete = async () => {
        const response = await deleteBsSampleSizeChart(selectedItem._id);
        if (response.success) {
            onDeleteClose();
            loadData();
        } else alert(response.error || 'Failed to delete sample size chart');
    };

    const handleRestore = async (id) => {
        const response = await restoreBsSampleSizeChart(id);
        if (response.success) loadData();
        else alert(response.error || 'Failed to restore sample size chart');
    };

    const renderForm = (formState, setter) => (
        <VStack spacing={4}>
            <FormControl>
                <FormLabel>Sample Type</FormLabel>
                <Select value={formState.sampleTypeId || ''} onChange={(e) => handleChange(setter, 'sampleTypeId', e.target.value)}>
                    <option value="">Select sample type</option>
                    {sampleTypes.map((type) => <option key={type._id} value={type._id}>{type.name}</option>)}
                </Select>
            </FormControl>
            <HStack w="full" spacing={4} align="start">
                <FormControl><FormLabel>Min Batch Size</FormLabel><Input type="number" value={formState.minBatchSize ?? 0} onChange={(e) => handleChange(setter, 'minBatchSize', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Max Batch Size</FormLabel><Input type="number" value={formState.maxBatchSize ?? 0} onChange={(e) => handleChange(setter, 'maxBatchSize', e.target.value)} /></FormControl>
                <FormControl><FormLabel>Required Sample Size</FormLabel><Input type="number" value={formState.requiredSampleSize ?? 0} onChange={(e) => handleChange(setter, 'requiredSampleSize', e.target.value)} /></FormControl>
            </HStack>
            <HStack w="full" spacing={4} align="start">
                <FormControl>
                    <FormLabel>Batch Unit</FormLabel>
                    <Select value={formState.batchUnit || 'ct'} onChange={(e) => handleChange(setter, 'batchUnit', e.target.value)}>
                        {UNIT_OPTIONS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                    </Select>
                </FormControl>
                <FormControl>
                    <FormLabel>Sample Unit</FormLabel>
                    <Select value={formState.sampleUnit || 'ct'} onChange={(e) => handleChange(setter, 'sampleUnit', e.target.value)}>
                        {UNIT_OPTIONS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                    </Select>
                </FormControl>
                <FormControl>
                    <FormLabel>Rounding Mode</FormLabel>
                    <Select value={formState.roundingMode || 'none'} onChange={(e) => handleChange(setter, 'roundingMode', e.target.value)}>
                        {ROUNDING_OPTIONS.map((mode) => <option key={mode} value={mode}>{mode}</option>)}
                    </Select>
                </FormControl>
            </HStack>
            <FormControl><FormLabel>Notes</FormLabel><Textarea value={formState.notes || ''} onChange={(e) => handleChange(setter, 'notes', e.target.value)} /></FormControl>
            <Checkbox isChecked={!!formState.isActive} onChange={(e) => handleChange(setter, 'isActive', e.target.checked)}>Active</Checkbox>
        </VStack>
    );

    if (loading) return <Center mt="20%"><Loading /></Center>;

    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                {!error ? (
                    <>
                        <TitleHeader title="BSS Sample Size Charts" buttonText={hasPermission('createBSSSampleSizeChart') ? 'Create New Chart' : undefined} onClick={hasPermission('createBSSSampleSizeChart') ? onCreateOpen : undefined} />
                        <HStack spacing={4} mb="4" align="end">
                            <FormControl>
                                <FormLabel>Search</FormLabel>
                                <Input placeholder="Search by sample type, unit, notes" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </FormControl>
                            <Checkbox isChecked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}>Show deleted</Checkbox>
                        </HStack>

                        {filteredItems.length ? (
                            <Table variant="simple" size="sm">
                                <Thead bg={coolors.theadBg}>
                                    <Tr>
                                        <Th>#</Th>
                                        <Th cursor="pointer" onClick={() => toggleSort('sampleType')}>Sample Type {sortField === 'sampleType' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}</Th>
                                        <Th cursor="pointer" onClick={() => toggleSort('minBatchSize')}>Range {sortField === 'minBatchSize' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}</Th>
                                        <Th>Sample Size</Th>
                                        <Th>Units</Th>
                                        <Th>Status</Th>
                                        <Th textAlign="right">Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {filteredItems.map((item, index) => (
                                        <Tr key={item._id} borderBottom={`2px solid ${coolors.borderColor}`} bg={index % 2 === 0 ? coolors.rowBgEven : coolors.rowBgOdd} color={coolors.textColor} _hover={{ bg: coolors.rowHoverBg, color: 'white' }}>
                                            <Td><PopoverId place="BSS Sample Size Charts" index={index + 1} content={item._id} /></Td>
                                            <Td>{item?.sampleTypeId?.name || '-'}</Td>
                                            <Td>{item.minBatchSize} - {item.maxBatchSize}</Td>
                                            <Td>{item.requiredSampleSize}</Td>
                                            <Td>{item.batchUnit} → {item.sampleUnit}</Td>
                                            <Td><Badge colorScheme={item.deletedAt ? 'red' : item.isActive ? 'green' : 'yellow'}>{item.deletedAt ? 'Deleted' : item.isActive ? 'Active' : 'Inactive'}</Badge></Td>
                                            <Td textAlign="right">
                                                <HStack justifyContent="flex-end" spacing={2}>
                                                    {hasPermission('viewBSSSampleSizeCharts') && <Button size="xs" colorScheme="green" onClick={() => openView(item)}><ViewIcon /></Button>}
                                                    {!item.deletedAt && hasPermission('updateBSSSampleSizeChart') && <Button size="xs" colorScheme="blue" onClick={() => openEdit(item)}><EditIcon /></Button>}
                                                    {!item.deletedAt && hasPermission('deleteBSSSampleSizeChart') && <Button size="xs" colorScheme="red" onClick={() => openDelete(item)}><DeleteIcon /></Button>}
                                                    {item.deletedAt && hasPermission('restoreBSSSampleSizeChart') && <Button size="xs" colorScheme="green" onClick={() => handleRestore(item._id)}><RepeatIcon /></Button>}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        ) : <AccessDenied msg="No BSS sample size charts found!" icon="info" />}
                    </>
                ) : <AccessDenied msg={error} />}
            </VStack>


            <Modal scrollBehavior="inside" size="3xl" isOpen={isViewOpen} onClose={onViewClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Sample Size Chart Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedItem && (
                            <VStack spacing={6} align="stretch">
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box><Text fontSize="sm" color="gray.500">Sample Type</Text><Text fontWeight="semibold">{selectedItem?.sampleTypeId?.name || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Status</Text><Badge colorScheme={selectedItem.deletedAt ? 'red' : selectedItem.isActive ? 'green' : 'yellow'}>{selectedItem.deletedAt ? 'Deleted' : selectedItem.isActive ? 'Active' : 'Inactive'}</Badge></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Min Batch Size</Text><Text>{selectedItem.minBatchSize ?? '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Max Batch Size</Text><Text>{selectedItem.maxBatchSize ?? '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Required Sample Size</Text><Text>{selectedItem.requiredSampleSize ?? '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Rounding Mode</Text><Text>{selectedItem.roundingMode || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Batch Unit</Text><Text>{selectedItem.batchUnit || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Sample Unit</Text><Text>{selectedItem.sampleUnit || '-'}</Text></Box>
                                </SimpleGrid>

                                <Box>
                                    <Text fontSize="sm" color="gray.500" mb={2}>Notes</Text>
                                    <Text whiteSpace="pre-wrap">{selectedItem.notes || '-'}</Text>
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onViewClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Confirm Deletion</ModalHeader>
                    <ModalBody>Are you sure you want to delete this sample size chart?</ModalBody>
                    <ModalFooter>
                        <Button colorScheme="red" mr={3} onClick={handleDelete}>Delete</Button>
                        <Button onClick={onDeleteClose}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" size="2xl" isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit Sample Size Chart</ModalHeader>
                    <ModalBody>{selectedItem && renderForm(selectedItem, setSelectedItem)}</ModalBody>
                    <ModalFooter>
                        <Button onClick={onClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleSave}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" size="2xl" isOpen={isCreateOpen} onClose={onCreateClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create Sample Size Chart</ModalHeader>
                    <ModalBody>{renderForm(newItem, setNewItem)}</ModalBody>
                    <ModalFooter>
                        <Button onClick={onCreateClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleCreate}>Create</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default BSSSampleSizeChartsPage;